import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from "../../services/api";
import Menu from "../../comum/Menu/menu.jsx";
import Footer from "../../comum/Footer";
import ModalImportacao from "./ModalConc/ModalConcImportacao";
import ModalTaxa from "./ModalConc/ModalConcTaxa";
import ModalManual from "./ModalConc/ModalConcManual";
import ModalAssociacao from "./ModalConc/ModalConcAssociacao";
import ModalExcluir from "./ModalConc/ModalConcExclui";
import { Modal, Spinner } from 'react-bootstrap';
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    FaFileExcel, FaStore,
    FaArrowUp, FaArrowDown,
    FaPercent,
    FaMoneyBillWave,
    FaLaptop,
    FaCopy,
    FaBalanceScale,
    FaFilter,
    FaSortAmountDown,
    FaGripVertical
} from "react-icons/fa";

export default function ConciliacaoCartao() {
    const navigate = useNavigate();

    // Estados de Controle de Modal [cite: 157-158]
    const [showModalImport, setShowModalImport] = useState(false);
    const [showModalTaxa, setShowModalTaxa] = useState(false);
    const [showModalManual, setShowModalManual] = useState(false);
    const [showModalAssociacao, setShowModalAssociacao] = useState(false);
    const [showModalExcluir, setShowModalExcluir] = useState(false);
    const [loading, setLoading] = useState(false);
    const [lista, setLista] = useState([]);

    // Estados de Totais [cite: 159]
    const [totais, setTotais] = useState({
        bruto: 0, taxa: 0, liquido: 0, software: 0,
        duplicidade: 0, liquido_duplicidade: 0, duplicidade_se: 0, valorse: 0
    });

    // ==========================================
    // CONTROLES DE GRID (FILTRO / SORT / DRAG)
    // ==========================================
    const [filtroGrid, setFiltroGrid] = useState({});
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    // Definição das Colunas com suporte a Visibilidade Dinâmica e Drag & Drop
    const [colunas, setColunas] = useState([
        { key: 'loja', label: 'Loja', visibleKey: 'checkLoja' },
        { key: 'datavenda', label: 'Data Venda', visibleKey: 'checkVenda', isDate: true },
        { key: 'roteamento', label: 'Roteamento', visibleKey: 'checkRoteamento' },
        { key: 'bandeira', label: 'Bandeira', visibleKey: 'checkBandeira' },
        { key: 'condicao', label: 'Condição', visibleKey: 'checkCondicao' },
        { key: 'datacredito', label: 'Crédito', visibleKey: 'checkCredito', isDate: true },
        { key: 'valorbruto', label: 'Bruto', alwaysVisible: true, isNumeric: true },
        { key: 'taxa', label: 'Taxa (%)', alwaysVisible: true, isNumeric: true },
        { key: 'valortaxa', label: 'Vlr. Taxa', alwaysVisible: true, isNumeric: true },
        { key: 'valorliq', label: 'Líquido', alwaysVisible: true, isNumeric: true },
        { key: 'valorse', label: 'Líquido SE', alwaysVisible: true, isNumeric: true }
    ]);

    // Drag & Drop de Colunas
    const handleDragStart = (e, index) => e.dataTransfer.setData("colIndex", index);
    const handleDrop = (e, targetIndex) => {
        const sourceIndex = e.dataTransfer.getData("colIndex");
        const newCols = [...colunas];
        const [movedCol] = newCols.splice(sourceIndex, 1);
        newCols.splice(targetIndex, 0, movedCol);
        setColunas(newCols);
    };

    // Lógica de Filtro Local (Valores Distintos)
    const getDistinctValues = (key, isDate) => {
        const values = lista.map(item => isDate ? formatarDataParaBR(item[key]) : item[key]);
        return [...new Set(values)].filter(Boolean).sort();
    };

    // Processamento da Lista (AutoFiltro + Ordenação Local)
    const listaExibicao = useMemo(() => {
        let dados = [...lista];

        // Aplica Filtros de Cabeçalho
        Object.keys(filtroGrid).forEach(key => {
            if (filtroGrid[key]) {
                dados = dados.filter(item => {
                    const col = colunas.find(c => c.key === key);
                    const val = col?.isDate ? formatarDataParaBR(item[key]) : item[key];
                    return String(val) === String(filtroGrid[key]);
                });
            }
        });

        // Aplica Ordenação Local
        if (sortConfig.key) {
            dados.sort((a, b) => {
                let v1 = a[sortConfig.key];
                let v2 = b[sortConfig.key];
                if (v1 < v2) return sortConfig.direction === 'asc' ? -1 : 1;
                if (v1 > v2) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return dados;
    }, [lista, filtroGrid, sortConfig, colunas]);

    const handleSort = (key) => {
        setSortConfig(prev => ({
            key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    // Verificação de Acesso [cite: 160-162]
    useEffect(() => {
        const usuario = localStorage.getItem("chapa");
        const permiteConciliacao = localStorage.getItem("CONCILIACARTAO");
        if (!usuario) { navigate("/arbor/login"); return; }
        if (permiteConciliacao !== "S") {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Não é permitido acessar o módulo de conciliação.',
                confirmButtonText: 'OK'
            }).then(() => { navigate("/arbor/home"); });
        }
    }, [navigate]);

    // Configuração de Datas e Filtros de Agrupamento [cite: 195-197]
    const agora = new Date();
    const hoje = agora.toISOString().split('T')[0];
    const primeiroDiaDoMes = new Date(agora.getFullYear(), agora.getMonth(), 1).toISOString().split('T')[0];

    const [filtros, setFiltros] = useState({
        dataInicio: primeiroDiaDoMes,
        dataFim: hoje,
        checkLoja: true,
        checkVenda: true,
        checkRoteamento: false,
        checkBandeira: false,
        checkCondicao: false,
        checkCredito: false
    });

    // Formatadores [cite: 198-204]
    const formatarNumero = (valor) => new Intl.NumberFormat('pt-BR', { style: 'decimal', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(valor || 0);
    const formatarMoeda = (valor) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(valor || 0));

    const formatarDataParaBR = (dataIso) => {
        if (!dataIso) return '';
        try {
            const limpa = dataIso.replace('Z', '');
            const d = new Date(limpa);
            return isNaN(d) ? dataIso : d.toLocaleDateString('pt-BR');
        } catch { return dataIso; }
    };

    const formatarDataFiltro = (dataIso) => {
        if (!dataIso) return '';
        const [ano, mes, dia] = dataIso.split('-');
        return `${dia}/${mes}/${ano}`;
    };

    const calcularDiferenca = () => {
        const liquido = Number(totais.liquido || 0);
        const liquidoDup = Number(totais.liquido_duplicidade || 0);
        const software = Number(totais.software || 0);
        const duplSe = Number(totais.duplicidade_se || 0);
        return ((liquido - liquidoDup) - software) - duplSe;
    };

    // Consulta API [cite: 207-214]
    const handleConsultar = async () => {
        const algumMarcado = filtros.checkLoja || filtros.checkVenda || filtros.checkRoteamento || filtros.checkBandeira || filtros.checkCondicao || filtros.checkCredito;
        if (!algumMarcado) { Swal.fire("Erro", "Selecione ao menos um agrupamento.", "error"); return; }
        setLoading(true);
        try {
            const params = {
                ...filtros,
                dataInicio: formatarDataFiltro(filtros.dataInicio),
                dataFim: formatarDataFiltro(filtros.dataFim)
            };
            const res = await api.get('/financeiro/conciliacao', { params });
            setLista(res.data?.dados || []);
            setTotais(res.data?.resumo || {});
            console.log(lista);
        } catch (err) {
            Swal.fire("Erro", "Falha ao consultar dados", "error");
        } finally {
            setTimeout(() => setLoading(false), 400);
        }
    };

    // Exportação Excel [cite: 163-194]
    const handleExportExcel = () => {
        if (!listaExibicao || listaExibicao.length === 0) {
            Swal.fire("Aviso", "Não há dados para exportar.", "warning");
            return;
        }
        try {
            const montarLinha = (row) => {
                const obj = {};
                obj["Cód. Cadastro"] = row.codicadastro || "";
                if (filtros.checkLoja) obj["Loja"] = row.loja;
                if (filtros.checkVenda) obj["Data Venda"] = formatarDataParaBR(row.datavenda);
                if (filtros.checkRoteamento) obj["Roteamento"] = row.roteamento;
                if (filtros.checkBandeira) obj["Bandeira"] = row.bandeira;
                if (filtros.checkCondicao) obj["Condição"] = row.condicao;
                if (filtros.checkCredito) obj["Crédito"] = formatarDataParaBR(row.datacredito);
                obj["Bruto"] = Number(row.valorbruto || 0);
                obj["Taxa (%)"] = Number(row.taxa || 0);
                obj["Vlr. Taxa"] = Number(row.valortaxa || 0);
                obj["Líquido"] = Number(row.valorliq || 0);
                obj["Líquido SE"] = Number(row.valorse || 0);
                return obj;
            };

            const dadosExcel = listaExibicao.map(montarLinha);
            const ws = XLSX.utils.json_to_sheet(dadosExcel);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Conciliacao");
            const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
            const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
            saveAs(blob, `Conciliacao_${filtros.dataInicio}_${filtros.dataFim}.xlsx`);
            Swal.fire("Sucesso", "Excel gerado com sucesso!", "success");
        } catch (err) {
            Swal.fire("Erro", "Falha ao exportar Excel.", "error");
        }
    };

    return (
        <div className="bg-light min-vh-100">
            <Menu modulo="ARBOR" />

            <Modal show={loading} centered backdrop="static" keyboard={false} size="sm">
                <Modal.Body className="text-center p-4">
                    <Spinner animation="border" variant="primary" className="mb-2" />
                    <div className="fw-bold">Consultando dados...</div>
                    <small className="text-muted">Aguarde um momento</small>
                </Modal.Body>
            </Modal>

            <main className="container-fluid" style={{ paddingTop: '95px', position: 'relative', zIndex: 1 }}>

                {/* Painel de Filtros Originais [cite: 217-253] */}
                <div className="card shadow-sm border-0 mb-3">
                    <div className="card-body py-2">
                        <div className="row g-2 align-items-end">
                            <div className="col-md-auto">
                                <label className="form-label small fw-bold mb-0">Data Início</label>
                                <input type="date" className="form-control form-control-sm" value={filtros.dataInicio} onChange={e => setFiltros({ ...filtros, dataInicio: e.target.value })} />
                            </div>
                            <div className="col-md-auto">
                                <label className="form-label small fw-bold mb-0">Data Fim</label>
                                <input type="date" className="form-control form-control-sm" value={filtros.dataFim} onChange={e => setFiltros({ ...filtros, dataFim: e.target.value })} />
                            </div>
                            <div className="col">
                                <label className="form-label small fw-bold mb-1">Agrupar por</label>
                                <div className="d-flex flex-wrap gap-2">
                                    {['Loja', 'Venda', 'Roteamento', 'Bandeira', 'Condicao', 'Credito'].map(f => (
                                        <div key={f}>
                                            <input type="checkbox" className="btn-check" id={`chk${f}`} checked={filtros[`check${f}`]} onChange={e => setFiltros({ ...filtros, [`check${f}`]: e.target.checked })} />
                                            <label htmlFor={`chk${f}`} className="btn btn-outline-secondary btn-sm py-1 px-2" style={{ fontSize: '0.75rem' }}>{f}</label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="col-md-auto d-flex gap-2">
                                <button className="btn btn-primary btn-sm fw-bold px-3" onClick={handleConsultar}>PESQUISAR</button>
                                <button className="btn btn-primary btn-sm fw-bold px-3" onClick={() => setShowModalManual(true)}>MANUAL</button>
                                <button className="btn btn-success btn-sm fw-bold px-3" onClick={() => setShowModalTaxa(true)}>TAXAS</button>
                                <button className="btn btn-warning btn-sm fw-bold px-3" onClick={() => setShowModalImport(true)}>IMPORTAR</button>
                                <button className="btn btn-dark btn-sm fw-bold px-3" onClick={() => setShowModalAssociacao(true)}>ASSOCIAR</button>
                                <button className="btn btn-outline-success btn-sm fw-bold px-3 d-flex align-items-center gap-2" onClick={handleExportExcel}><FaFileExcel /> EXCEL</button>
                                <button className="btn btn-danger btn-sm fw-bold px-3" onClick={() => setShowModalExcluir(true)}>Limpar</button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tabela com AutoFiltro e Drag & Drop [cite: 254-282] */}
                <div className="card shadow-sm border-0 mb-2">
                    <div className="table-responsive" style={{ maxHeight: '55vh' }}>
                        <table className="table table-sm table-hover table-bordered mb-0">
                            <thead className="table-dark sticky-top text-center">
                                <tr>
                                    {colunas.map((col, idx) => {
                                        if (!col.alwaysVisible && !filtros[col.visibleKey]) return null;
                                        return (
                                            <th
                                                key={col.key}
                                                draggable
                                                onDragStart={(e) => handleDragStart(e, idx)}
                                                onDragOver={(e) => e.preventDefault()}
                                                onDrop={(e) => handleDrop(e, idx)}
                                                style={{ cursor: 'move', minWidth: '120px' }}
                                            >
                                                <div className="d-flex align-items-center justify-content-between px-1 mb-1">
                                                    <FaGripVertical size={10} className="opacity-50" />
                                                    <span style={{ fontSize: '0.75rem' }}>{col.label}</span>
                                                    <FaSortAmountDown
                                                        size={12}
                                                        className="ms-2 cursor-pointer"
                                                        onClick={() => handleSort(col.key)}
                                                    />
                                                </div>
                                                {/* AutoFiltro Select (Distinct) */}
                                                <select
                                                    className="form-select form-select-sm py-0 shadow-none"
                                                    style={{ fontSize: '0.65rem', backgroundColor: '#444', color: '#fff', border: '1px solid #666' }}
                                                    value={filtroGrid[col.key] || ""}
                                                    onChange={(e) => setFiltroGrid({ ...filtroGrid, [col.key]: e.target.value })}
                                                >
                                                    <option value="">(Tudo)</option>
                                                    {getDistinctValues(col.key, col.isDate).map(v => (
                                                        <option key={v} value={v}>{v}</option>
                                                    ))}
                                                </select>
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody className="small">
                                {listaExibicao.map((row, i) => (
                                    <tr key={i} className="text-center align-middle">
                                        {colunas.map(col => {
                                            if (!col.alwaysVisible && !filtros[col.visibleKey]) return null;

                                            if (col.key === 'valorse') {
                                                return (
                                                    <td key={col.key} className="text-end fw-bold text-success">
                                                        <div className="d-flex align-items-center justify-content-end gap-1">
                                                            {row.valorse > row.valorliq && <FaArrowUp size={12} title="Maior que o Líquido" />}
                                                            {row.valorse < row.valorliq && <FaArrowDown size={12} className="text-danger" title="Menor que o Líquido" />}
                                                            <span>{formatarNumero(row.valorse)}</span>
                                                        </div>
                                                    </td>
                                                );
                                            }

                                            let valor = row[col.key];
                                            if (col.isDate) valor = formatarDataParaBR(valor);
                                            if (col.isNumeric) valor = formatarNumero(valor);

                                            return (
                                                <td key={col.key} className={col.isNumeric ? "text-end px-2" : ""}>
                                                    {valor}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Dashboard de Totais [cite: 283-287] */}
                <div className="sticky-bottom bg-light pt-2 pb-2 border-top">
                    <div className="row g-2">
                        <CardTotal icon={<FaMoneyBillWave />} label="BRUTO" value={totais.bruto} bg="primary" />
                        <CardTotal icon={<FaPercent />} label="TAXA" value={totais.taxa} bg="danger" />
                        <CardTotal icon={<FaStore />} label="LÍQUIDO" value={totais.liquido} bg="success" />
                        <CardTotal icon={<FaLaptop />} label="LÍQUIDO SE" value={totais.software} bg="warning" />
                        <CardTotal icon={<FaCopy />} label="DUPLICIDADE" value={totais.duplicidade} bg="secondary" />
                        <CardTotal
                            icon={<FaBalanceScale />}
                            label="DIFERENÇA"
                            value={calcularDiferenca()}
                            bg={calcularDiferenca() === 0 ? "info" : "dark"}
                            highlight={calcularDiferenca() !== 0}
                        />
                    </div>
                </div>
            </main>

            {/* Modais de Funcionalidade [cite: 288-292] */}
            <ModalImportacao show={showModalImport} onHide={() => setShowModalImport(false)} />
            <ModalTaxa show={showModalTaxa} onHide={() => setShowModalTaxa(false)} />
            <ModalManual
                show={showModalManual}
                onHide={() => setShowModalManual(false)}
                onSuccess={handleConsultar}
                dataInicio={formatarDataFiltro(filtros.dataInicio)} // Passando a data de início
                dataFim={formatarDataFiltro(filtros.dataFim)}       // Passando a data de fim
            />
            <ModalAssociacao show={showModalAssociacao} onHide={() => setShowModalAssociacao(false)} onSuccess={handleConsultar} />
            <ModalExcluir show={showModalExcluir} onHide={() => setShowModalExcluir(false)} onSuccess={handleConsultar} />

            <Footer />
        </div>
    );
}

// Componente Interno CardTotal [cite: 293-302]
function CardTotal({ label, value, bg, icon, highlight }) {
    const formatarMoedaLocal = (valor) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor || 0);
    return (
        <div className="col-6 col-md">
            <div className={`card border-0 shadow-sm bg-${bg} text-white h-100`} style={{ borderRadius: '12px', transform: highlight ? 'scale(1.03)' : 'scale(1)', transition: 'all .25s ease' }}>
                <div className="card-body p-2">
                    <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
                        <div style={{ fontSize: '0.9rem' }}>{icon}</div>
                        <div style={{ fontSize: '0.70rem', fontWeight: 700 }}>{label}</div>
                    </div>
                    <div className="text-center" style={{ fontSize: '1.15rem', fontWeight: 900 }}>{formatarMoedaLocal(value)}</div>
                    {highlight && (
                        <div className="text-center mt-1">
                            <small style={{ fontSize: '0.65rem', opacity: 0.9 }}>⚠ Divergência detectada</small>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}